import { useContext, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';

import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import Box from 'components/ui/box';

import FeedbackImg from 'assets/img/comportamento/bg-feedback-moedas.png';

interface Props {
  callback: (obj: { id: string; message?: string }) => void;
  id: string;
  status: string | undefined;
  data?: any;
}

const useFocusTrap = (ref: React.RefObject<HTMLDivElement>, isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen || !ref.current) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // ESC fecha a modal
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      // Sempre previne o comportamento padrão do TAB
      event.preventDefault();

      const focusableElements = Array.from(
        ref.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ) as HTMLElement[];

      if (focusableElements.length === 0) return;

      const activeElement = document.activeElement as HTMLElement;
      let currentIndex = focusableElements.indexOf(activeElement);

      // Se não encontrou o elemento atual na lista, começa do início
      if (currentIndex === -1) {
        currentIndex = event.shiftKey ? 0 : -1;
      }

      let nextIndex: number;
      if (event.shiftKey) {
        // SHIFT+TAB: vai para o anterior (ou último se estiver no primeiro)
        nextIndex = currentIndex <= 0 ? focusableElements.length - 1 : currentIndex - 1;
      } else {
        // TAB: vai para o próximo (ou primeiro se estiver no último)
        nextIndex = currentIndex >= focusableElements.length - 1 ? 0 : currentIndex + 1;
      }

      focusableElements[nextIndex].focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, ref, onClose]);
};

interface Props {
  callback: (obj: { id: string; message?: string }) => void;
  id: string;
  status: string | undefined;
  data?: any;
}

export default function ModalKnown(props: Props) {
  ReactModal.setAppElement('#modal');

  const modalID = 'validation-known';

  const { openModal, closeModal } = useModal();
  const { callback, status, id, data } = props;

  const [modalIsOpen, setIsOpen] = useState<boolean>(false);
  const [success, setSuccess] = useState(false);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  function handleCloseModal() {
    closeModal();
  }

  useFocusTrap(modalContentRef, modalIsOpen, handleCloseModal);

  function afterOpenModal() {
    if (!success && confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }

  useEffect(() => {
    if (typeof id === 'string' && typeof openModal === 'string' && openModal === `${modalID}-${id}`) {
      setSuccess(false);
      setIsOpen(true);
    } else {
      setSuccess(false);
      setIsOpen(false);
    }
  }, [id, openModal]);

  if (!modalIsOpen) return <></>;

  return (
    <>
      <div
        ref={liveRegionRef}
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      />
      <ReactModal
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={true}
        isOpen={modalIsOpen}
        onAfterOpen={afterOpenModal}
        onRequestClose={handleCloseModal}
        contentLabel="Ciência de Ação ECOA"
        aria-modal="true"
        aria-labelledby="known-modal-title"
        portalClassName="relative z-50"
        overlayClassName="fixed z-100 top-0 left-0 bg-black bg-opacity-60 flex flex-col items-center w-screen h-screen p-6 pt-[10vh] overflow-y-auto"
        contentElement={() => (
          <div ref={modalContentRef}>
            <Box className={`w-[90%] lg:w-auto lg:max-w-[600px]`}>
              <Button
              color="primary"
              className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center"
              aria-label="Fechar ciência de ação"
              onClick={() => {
                closeModal();
                if (!!success) callback({ id: 'open_feedback' });
              }}
            >
              <IoIosClose color="white" size={40} />
            </Button>

            {!!success ? (
              <>
                <div className="w-full lg:w-[260px] flex flex-col items-center justify-center space-y-8">
                  <div className="flex items-center justify-center">
                    <img src={FeedbackImg} alt="Ação marcada como ciente com sucesso" className="w-[173px] block" />
                  </div>
                  <h4 id="known-modal-title" className="block text-2xl font-semibold text-yellow text-center">Mandou bem!</h4>
                  <p className="text-sm font-white font-thin text-center block">Essa ação foi marcada como ciente.</p>
                  <button
                    className={`text-white bg-slate-blue text-sm inline-flex items-center justify-center font-semibold uppercase rounded py-3 px-4 leading-none`}
                    onClick={() => {
                      closeModal();
                      callback({ id: 'open_feedback' });
                    }}
                  >
                    Fechar
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-full lg:w-[536px] flex flex-col items-center justify-center space-y-8">
                  <h4 id="known-modal-title" className="block text-center text-yellow text-lg font-semibold leading-none">Ciente da Ação ECOA</h4>
                  <p className="text-alice-blue text-base font-thin block">Deseja confirmar a ciência da Ação ECOA?</p>
                  <button
                    ref={confirmBtnRef}
                    className={`text-white bg-slate-blue text-sm inline-flex items-center justify-center font-semibold uppercase rounded py-3 px-4 leading-none`}
                    onClick={() => {
                      callback({ id: 'known' });
                      setSuccess(true);
                      if (liveRegionRef.current) {
                        liveRegionRef.current.textContent = 'Ação marcada como ciente com sucesso.';
                      }
                    }}
                  >
                    CONFIRMAR
                  </button>
                </div>
              </>
            )}
            </Box>
          </div>
        )}
      />
    </>
  );
}
